import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Alert } from "@replohq/lattice/blocks/alert/Alert";
import { SelectionItem } from "@replohq/lattice/blocks/selection/SelectionItem";
import { Button } from "@replohq/lattice/components/button/Button";
import { Modal } from "@replohq/lattice/components/modal/Modal";
import { Textarea } from "@replohq/lattice/components/textarea/Textarea";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useApplySubscriptionChangeMutation from "@v2/features/billing/hooks/useApplySubscriptionChangeMutation";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useSubscriptionDetails } from "@v2/hooks/subscription";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { billingRouter } from "@v2/trpc/routes/billing";
import { format } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { getBillingPlanInfoByTier } from "schemas/billing";
import { z } from "zod";

const CANCEL_REASONS = [
  "Store Closing",
  "Poor Support",
  "Too Expensive",
  "Difficult to Learn",
  "Missing Key Features",
  "Found an Alternative",
  "No Ongoing Value",
  "Other Reason",
] as const;

const cancelPlanFormSchema = z.object({
  selectedReasons: z
    .array(z.string())
    .min(1, "Please select at least one reason"),
  textFeedback: z.string().optional(),
});

type CancelPlanFormValues = z.infer<typeof cancelPlanFormSchema>;

interface CancelPlanModalProps {
  source: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CancelPlanModal: React.FC<CancelPlanModalProps> = ({
  source,
  open,
  onOpenChange,
}) => {
  const workspaceId = useCurrentWorkspaceId();
  const { user } = useCurrentUser();
  const { data: activeSubscription } = useSubscriptionDetails();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    getValues,
    reset,
  } = useForm<CancelPlanFormValues>({
    resolver: zodResolver(cancelPlanFormSchema),
    mode: "onChange",
    defaultValues: {
      selectedReasons: [],
      textFeedback: "",
    },
  });

  const { applySubscriptionChange } = useApplySubscriptionChangeMutation();

  const [currentStep, setCurrentStep] = React.useState<"reasons" | "feedback">(
    "reasons",
  );

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setCurrentStep("reasons");
      reset();
    }
    onOpenChange(isOpen);
  };

  const { data: nextBillingDate } =
    billingRouter.getNextCorePlanBillingDate.useQuery(
      workspaceId && activeSubscription?.id
        ? {
            workspaceId,
            subscriptionId: activeSubscription.id,
          }
        : skipToken,
    );

  const formattedEndDate = React.useMemo(() => {
    if (nextBillingDate) {
      try {
        return format(new Date(nextBillingDate), "MMMM do, yyyy");
      } catch {
        return "the end of your current billing period";
      }
    }
    return "the end of your current billing period";
  }, [nextBillingDate]);

  // NOTE (Cole 2025-09-11): There must be an active subscription to submit an update.
  if (!activeSubscription || !workspaceId) {
    return null;
  }

  const cancelPlan = async () => {
    await applySubscriptionChange({
      workspaceId,
      billingTier: "free",
      billingInterval: activeSubscription.billingInterval,
      source,
      addOns: [],
    });
  };

  const onSubmit = async (formData: CancelPlanFormValues) => {
    analytics.logEvent("billing.cancelModal.submittedFeedbackStep", {
      currentPlan: activeSubscription.billingTier,
      feedback: formData.textFeedback,
    });

    await cancelPlan();

    handleOpenChange(false);
  };

  const reasonsStepTitle = user?.firstName
    ? `${user.firstName}, before you go...`
    : "Before You Go...";

  const nameOfPlanBeingCancelled = getBillingPlanInfoByTier(
    activeSubscription.billingTier,
  ).displayName;

  const formSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void handleSubmit(onSubmit)(event);
  };

  return (
    <form onSubmit={formSubmit}>
      <Modal
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen && !isSubmitting) {
            handleOpenChange(false);
          }
        }}
        title={
          currentStep === "reasons"
            ? reasonsStepTitle
            : "We're sorry to see you go!"
        }
        footer={
          <div className="flex flex-row justify-end gap-2">
            <Button
              variant="secondary"
              size="base"
              onClick={() => {
                if (!isSubmitting) {
                  handleOpenChange(false);
                }
              }}
            >
              Nevermind
            </Button>

            <Button
              variant="primary"
              size="base"
              onClick={() => {
                if (isSubmitting) {
                  return;
                }
                if (currentStep === "reasons") {
                  void trigger("selectedReasons").then((isValid) => {
                    if (isValid) {
                      const selectedReasons = getValues("selectedReasons");
                      analytics.logEvent(
                        "billing.cancelModal.submittedReasonStep",
                        {
                          currentPlan: activeSubscription.billingTier,
                          reason: selectedReasons.join(", "),
                        },
                      );
                      setCurrentStep("feedback");
                    }
                  });
                } else {
                  void handleSubmit(onSubmit)();
                }
              }}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {currentStep === "reasons" ? "Cancel Plan" : "Finish"}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col -mt-4 gap-4">
          {currentStep === "reasons" ? (
            <>
              <p className="text-muted text-sm">
                If you don't mind sharing, we'd love to hear why you're leaving
                Replo.
              </p>
              {errors.selectedReasons && (
                <Alert
                  variant="error"
                  description={errors.selectedReasons.message}
                />
              )}
              <img
                src="/images/billing/cancel-plan-sad-globes.png"
                role="presentation"
                className="mb-2"
              />
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  name="selectedReasons"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      {CANCEL_REASONS.map((reason) => (
                        <SelectionItem
                          key={reason}
                          type="checkbox"
                          variant="outline"
                          id={reason}
                          label={reason}
                          checked={value.includes(reason)}
                          onCheckedChange={(checked) => {
                            onChange(
                              checked
                                ? [...value, reason]
                                : value.filter((item) => item !== reason),
                            );
                          }}
                        />
                      ))}
                    </>
                  )}
                />
              </div>
            </>
          ) : (
            <>
              <p className="text-muted text-sm">
                We appreciate you trying Replo. Share any feedback with us -
                we'd love to see you back someday.
              </p>
              <img
                src="/images/billing/cancel-plan-sad-balloons.png"
                role="presentation"
                className=""
              />
              <Controller
                name="textFeedback"
                control={control}
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <Textarea
                    ref={ref}
                    size="base"
                    variant="long"
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={onBlur}
                    placeholder="Anything you can share about your decision to cancel will be helpful for our small team."
                    autoFocus
                  />
                )}
              />

              <Alert
                variant="info"
                description={`You'll keep access to your Replo ${nameOfPlanBeingCancelled} plan until ${formattedEndDate}.`}
              />
            </>
          )}
        </div>
      </Modal>
    </form>
  );
};
